import React, { Component } from 'react';
import { ChromePicker } from 'react-color';
import { twMerge } from 'tailwind-merge';

import { ChevronDown, ChevronUp } from '@videoblocks/react-icons';

import Collapse from '../../../../common/components/Collapse/Collapse';

import './ColorFilter.less';

interface Props {
  name: string;
  filterName: string;
  value: string;
  onChange: (string, value: string) => void;
  isCollapsed?: boolean;
  toggleCollapsed: () => void;
}

interface State {
  inputColorCode: string;
  isCollapsed: boolean;
  isClient: boolean;
}

export default class ColorFilter extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      // We keep the interim color code in local state because color changes are only emitted when a full 6-character color code has been entered
      inputColorCode: this.ensureColorWithoutLeader(props.value),
      isCollapsed: this.props.isCollapsed,
      isClient: false, // SSR Color picker makes duplicate calls to search page
    };
  }

  static getDerivedStateFromProps(props, prevState) {
    if (props.value !== prevState.inputColorCode) {
      return {
        inputColorCode: props.value,
      };
    } else {
      return {};
    }
  }

  componentDidMount() {
    this.setState({ isClient: true });
  }

  render() {
    const selectedColorCode = this.state.inputColorCode;

    const { name, isCollapsed, toggleCollapsed } = this.props;

    const htmlSafeName = this.getHtmlSafeName(name);

    return (
      <div
        id={`filter-group-${htmlSafeName}`}
        className={isCollapsed ? 'collapsed' : 'expanded'}
      >
        <h3
          role="button"
          tabIndex={0}
          className="heading"
          onKeyDown={toggleCollapsed}
          onClick={toggleCollapsed}
          aria-controls={htmlSafeName}
          aria-expanded={isCollapsed ? 'false' : 'true'}
        >
          {this.props.name}
          {this.getArrowIcon(isCollapsed)}
        </h3>

        <Collapse isCollapsed={isCollapsed} controlId={htmlSafeName}>
          <div className="color-filter-wrapper extended">
            {this.state.isClient && (
              <ChromePicker
                color={selectedColorCode}
                onChangeComplete={(color, e) =>
                  this.handleColorChanged(color.hex, e)
                }
                disableAlpha={true}
              />
            )}

            <div className={twMerge(selectedColorCode.length <= 0 && 'hide')}>
              <a
                key="clear-color-filter"
                className="clear-color-filter"
                href="#"
                onKeyDown={(e) => this.handleColorChanged('', e)}
                onClick={(e) => this.handleColorChanged('', e)}
              >
                Reset Color
              </a>
            </div>
          </div>
        </Collapse>

        {this.showSelectedItemTextIfOptionsAreCollapsed()}
      </div>
    );
  }

  handleColorChanged = (color, e) => {
    e.preventDefault();

    const value = color ? this.ensureColorWithoutLeader(color) : '';

    // if someone moves the slider without the color being set, we don't want to fire a search
    if (this.props.value === '' && value === '000000') {
      return;
    }

    this.setState({
      inputColorCode:
        value !== '' ? this.ensureColorWithLeader(color) : '#000000',
    });

    const colorIsEmptyOrComplete = value.length === 6 || value.length === 0;
    if (colorIsEmptyOrComplete) {
      this.props.onChange(this.props.filterName, value);
    }
  };

  showSelectedItemTextIfOptionsAreCollapsed() {
    const { isCollapsed, inputColorCode } = this.state;

    if (isCollapsed && inputColorCode) {
      const color = this.ensureColorWithLeader(this.state.inputColorCode);
      let colorPreviewClassName = 'color-preview';
      if (!color) {
        colorPreviewClassName += ' invisible';
      }
      return (
        <div className="collapsible-filter-selected-option">
          <span
            className={colorPreviewClassName}
            style={{ backgroundColor: color }}
          />
          {color}
        </div>
      );
    } else {
      return null;
    }
  }

  ensureColorWithLeader(color) {
    return color.length > 0 && color.indexOf('#') === -1 ? '#' + color : color;
  }

  ensureColorWithoutLeader(color) {
    return color.indexOf('#') !== -1 ? color.substring(1) : color;
  }

  getHtmlSafeName(rawName) {
    return rawName.replace(/\W/g, '');
  }

  getArrowIcon(isCollapsed) {
    if (isCollapsed) {
      return <ChevronDown className="w-3 h-3 m-1.5" />;
    } else {
      return <ChevronUp className="w-3 h-3 m-1.5" />;
    }
  }
}
